import { Grid } from "@material-ui/core";
import { omit, mapValues } from "lodash";
import styled from "styled-components";
import { FunctionComponent } from "react";
import { Controller, useForm } from "react-hook-form";
import { CookiePreferences as CookiePreferencesType } from "../../store/cookie-policy/models";
import { keys, mapObject } from "../../utils/utils";
import { PrimaryButton } from "../button";
import { Form, FormProps } from "../forms";
import { Checkbox } from "../forms/checkbox";
import { useSelector } from "react-redux";
import { AppStore } from "../../store";
import i18n from "../../i18n";
import { useTranslation } from "react-i18next";

type CookiePreferencesFormType = Record<keyof CookiePreferencesType, boolean>;

const allFields: Record<keyof CookiePreferencesType, string> = {
  ad_storage: "ad-storage",
  analytics_storage: "analytics-storage",
  functionality_storage: "functionality-storage",
  personalization_storage: "personalization-storage",
  security_storage: "security-storage",
};

const Wrapper = styled.div`
  padding: 2em 4vw 4em;
  flex-shrink: 1;
  overflow: auto;
`;

const Item = styled.div`
  &:not(:last-child) {
    margin-bottom: 2em;
  }
`;

export const CookiePreferences: FunctionComponent<
  FormProps & {
    onSelectPreferences: (c: CookiePreferencesType) => void;
  }
> = (props) => {
  const defaultValues = useSelector<AppStore, CookiePreferencesType>(
    (store) => store.cookiePolicy.preferences
  );
  const {
    handleSubmit,
    control,
    setValue,
  } = useForm<CookiePreferencesFormType>({
    defaultValues: mapValues(defaultValues, (x) => x === "granted"),
  });

  const { t } = useTranslation("cookie-policy");

  const { onSelectPreferences } = props;
  const propsToPass = omit(props, "onSelectPreferences");

  return (
    <Form
      {...propsToPass}
      onSubmit={handleSubmit((data) =>
        onSelectPreferences(
          mapValues(data, (val) => (val ? "granted" : "denied"))
        )
      )}
    >
      <Wrapper>
        {mapObject(allFields, (description, f) => (
          <Item key={f}>
            <Controller
              control={control}
              name={f}
              render={({ field }) => (
                <Checkbox
                  {...field}
                  checked={field.value || false}
                  label={t(description)}
                />
              )}
            />
          </Item>
        ))}
      </Wrapper>
      <Grid container spacing={3}>
        <Grid item xs={12} lg={4}>
          <PrimaryButton
            variant="outlined"
            onClick={() => keys(allFields).forEach((x) => setValue(x, false))}
            fullWidth
          >
            {t("unselect-all")}
          </PrimaryButton>
        </Grid>
        <Grid item xs={12} lg={4}>
          <PrimaryButton
            onClick={() => keys(allFields).forEach((x) => setValue(x, true))}
            fullWidth
          >
            {t("select-all")}
          </PrimaryButton>
        </Grid>
        <Grid item xs={12} lg={4}>
          <PrimaryButton type="submit" fullWidth>
            {t("confirm-selection")}
          </PrimaryButton>
        </Grid>
      </Grid>
    </Form>
  );
};
